import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, ContentCopy, Delete, Edit, Webhook } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { DynamicDataGrid } from './common/DynamicContainer';
import { toast } from 'react-toastify';
import {
  BACKEND_API,
  buildAgentLabelMap,
  connectorLabel,
  fetchWebhookRouteDestinations,
  getAuthHeaders,
  hubFlowLabel,
  MASTER_CONNECTOR_API,
  normalizeName,
  publicIngressUrl,
  registryEventLabel,
  resolveAccountIdentifier,
  staticTaskPickerLabel,
  statusColor,
  syncFlowLabel,
  tenantId,
  webhookApiBase,
} from './webhook/webhookApi';

const WebhooksManager = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [masterConnectors, setMasterConnectors] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [saving, setSaving] = useState(false);

  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [editingSubscriptionId, setEditingSubscriptionId] = useState(null);
  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [editingRouteId, setEditingRouteId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [subscriptionForm, setSubscriptionForm] = useState({
    application_name: '',
    ctd_id: '',
    account_identifier: '',
    enabled: true,
  });

  const [tenantConnectors, setTenantConnectors] = useState([]);
  const [loadingConnectors, setLoadingConnectors] = useState(false);
  const [resolvingAccount, setResolvingAccount] = useState(false);

  const [routeDestinations, setRouteDestinations] = useState({
    tenantAgents: [],
    staticTasks: [],
    syncFlows: [],
    hubFlows: [],
  });
  const [loadingRouteDestinations, setLoadingRouteDestinations] = useState(false);

  const [routeForm, setRouteForm] = useState({
    event_key: '',
    application_name: '',
    event_name: '',
    target_type: 'TASK',
    selected_task_id: '',
    selected_flow_id: '',
    execution_mode: 'async',
    enabled: true,
  });
  const [routeLogDrawerOpen, setRouteLogDrawerOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedRouteLogs, setSelectedRouteLogs] = useState([]);
  const [selectedRouteSummary, setSelectedRouteSummary] = useState({
    total_events: 0,
    forwarded_events: 0,
    failed_events: 0,
    received_events: 0,
    last_24h_events: 0,
  });
  const [loadingRouteLogs, setLoadingRouteLogs] = useState(false);
  const [payloadDialogOpen, setPayloadDialogOpen] = useState(false);
  const [payloadDialogTitle, setPayloadDialogTitle] = useState('');
  const [payloadDialogContent, setPayloadDialogContent] = useState('');

  const loadMasterConnectors = useCallback(async () => {
    try {
      const res = await fetch(MASTER_CONNECTOR_API, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMasterConnectors(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setMasterConnectors([]);
    }
  }, []);

  const loadRegistry = useCallback(async () => {
    try {
      const res = await fetch(`${webhookApiBase()}/registry`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setRegistry(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setRegistry([]);
    }
  }, []);

  const loadSubscriptions = useCallback(async () => {
    const tid = tenantId();
    if (!tid) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${webhookApiBase()}/tenant-endpoints?tenant_id=${encodeURIComponent(tid)}`,
        { headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSubscriptions(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load subscriptions');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRoutes = useCallback(async () => {
    const tid = tenantId();
    if (!tid) return;
    try {
      const res = await fetch(
        `${webhookApiBase()}/route-mappings?tenant_id=${encodeURIComponent(tid)}`,
        { headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRoutes(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load route mappings');
      setRoutes([]);
    }
  }, []);

  useEffect(() => {
    loadMasterConnectors();
    loadRegistry();
    loadSubscriptions();
    loadRoutes();
  }, [loadMasterConnectors, loadRegistry, loadSubscriptions, loadRoutes]);

  const globalApplications = useMemo(() => {
    const apps = registry.map((r) => r.application_name).filter(Boolean);
    return [...new Set(apps)].sort();
  }, [registry]);

  const eventOptions = useMemo(
    () => registry
      .filter((r) => r.enabled !== false)
      .map((r) => ({
        key: `${r.application_name}:${r.event_name}`,
        event_name: r.event_name,
        application_name: r.application_name,
        label: registryEventLabel(r),
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [registry],
  );

  const loadRouteDestinations = useCallback(async () => {
    const tid = tenantId();
    if (!tid) return;
    setLoadingRouteDestinations(true);
    try {
      const data = await fetchWebhookRouteDestinations(tid);
      setRouteDestinations(data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load route destinations');
      setRouteDestinations({ tenantAgents: [], staticTasks: [], syncFlows: [], hubFlows: [] });
    } finally {
      setLoadingRouteDestinations(false);
    }
  }, []);

  const agentLabelMap = useMemo(
    () => buildAgentLabelMap(routeDestinations.tenantAgents),
    [routeDestinations.tenantAgents],
  );

  const taskPickerOptions = useMemo(
    () => routeDestinations.staticTasks
      .map((task) => ({
        id: task.staticTaskId,
        label: staticTaskPickerLabel(task, agentLabelMap),
        task,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [routeDestinations.staticTasks, agentLabelMap],
  );

  const openRouteDialog = async () => {
    setEditingRouteId(null);
    setRouteForm({
      event_key: '',
      application_name: '',
      event_name: '',
      target_type: 'TASK',
      selected_task_id: '',
      selected_flow_id: '',
      execution_mode: 'async',
      enabled: true,
    });
    setRouteDialogOpen(true);
    await loadRouteDestinations();
  };

  const openEditRouteDialog = async (row) => {
    setEditingRouteId(row.id);
    const targetType = row.target_type || 'TASK';
    const eventKey = row.application_name && row.event_name
      ? `${row.application_name}:${row.event_name}`
      : '';
    setRouteForm({
      event_key: eventKey,
      application_name: row.application_name || '',
      event_name: row.event_name || '',
      target_type: targetType,
      selected_task_id: targetType === 'TASK' ? String(row.target_reference_id || '') : '',
      selected_flow_id: targetType !== 'TASK' ? String(row.target_reference_id || '') : '',
      execution_mode: row.execution_mode || 'async',
      enabled: row.enabled !== false,
    });
    setRouteDialogOpen(true);
    await loadRouteDestinations();
  };

  const loadConnectorsForApplication = useCallback(async (applicationName) => {
    const tid = tenantId();
    if (!tid || !applicationName) {
      setTenantConnectors([]);
      return;
    }
    const mc = masterConnectors.find((c) => normalizeName(c.mcName || '') === normalizeName(applicationName));
    if (!mc?.mcId) {
      setTenantConnectors([]);
      return;
    }
    setLoadingConnectors(true);
    try {
      const res = await fetch(
        `${BACKEND_API}synctrack/sync-connectors/mc/${mc.mcId}/tenant/${encodeURIComponent(tid)}`,
        { headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTenantConnectors(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setTenantConnectors([]);
      toast.error('Failed to load connector tokens');
    } finally {
      setLoadingConnectors(false);
    }
  }, [masterConnectors]);

  const handleSubscriptionApplicationChange = async (applicationName) => {
    setSubscriptionForm({
      application_name: applicationName,
      ctd_id: '',
      account_identifier: '',
      enabled: true,
    });
    await loadConnectorsForApplication(applicationName);
  };

  const handleConnectorSelect = async (ctdId) => {
    setSubscriptionForm((f) => ({ ...f, ctd_id: ctdId }));
    if (!ctdId) {
      setSubscriptionForm((f) => ({ ...f, account_identifier: '' }));
      return;
    }
    setResolvingAccount(true);
    try {
      const account = await resolveAccountIdentifier(ctdId);
      if (account) {
        setSubscriptionForm((f) => ({ ...f, ctd_id: ctdId, account_identifier: account }));
      } else {
        toast.info('Could not auto-detect account — enter realm/email/workspace manually');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to read account from connector token');
    } finally {
      setResolvingAccount(false);
    }
  };

  const openSubscriptionDialog = () => {
    setEditingSubscriptionId(null);
    setSubscriptionForm({ application_name: '', ctd_id: '', account_identifier: '', enabled: true });
    setTenantConnectors([]);
    setSubscriptionDialogOpen(true);
  };

  const openEditSubscriptionDialog = async (row) => {
    setEditingSubscriptionId(row.id);
    setSubscriptionForm({
      application_name: row.application_name || '',
      ctd_id: row.ctd_id || '',
      account_identifier: row.account_identifier || '',
      enabled: row.enabled !== false,
    });
    setSubscriptionDialogOpen(true);
    await loadConnectorsForApplication(row.application_name || '');
  };

  const handleSaveSubscription = async () => {
    const tid = tenantId();
    if (!tid) {
      toast.error('Tenant not found');
      return;
    }
    if (!subscriptionForm.application_name.trim() || !subscriptionForm.account_identifier.trim()) {
      toast.error('Application and account identifier are required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        tenant_id: Number(tid),
        application_name: normalizeName(subscriptionForm.application_name),
        ctd_id: subscriptionForm.ctd_id || null,
        account_identifier: subscriptionForm.account_identifier.trim(),
        enabled: subscriptionForm.enabled,
      };
      const url = editingSubscriptionId
        ? `${webhookApiBase()}/tenant-endpoints/${encodeURIComponent(editingSubscriptionId)}?tenant_id=${encodeURIComponent(tid)}`
        : `${webhookApiBase()}/tenant-endpoints`;
      const res = await fetch(url, {
        method: editingSubscriptionId ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(editingSubscriptionId ? 'Subscription updated' : 'Subscription created');
      setSubscriptionDialogOpen(false);
      setEditingSubscriptionId(null);
      await loadSubscriptions();
    } catch (e) {
      toast.error(e.message || 'Failed to save subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubscription = async (row) => {
    const tid = tenantId();
    if (!tid || !row?.id) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${webhookApiBase()}/tenant-endpoints/${encodeURIComponent(row.id)}?tenant_id=${encodeURIComponent(tid)}`,
        { method: 'DELETE', headers: getAuthHeaders() },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success('Subscription deleted');
      setDeleteConfirm(null);
      await loadSubscriptions();
    } catch (e) {
      toast.error(e.message || 'Failed to delete subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRoute = async () => {
    const tid = tenantId();
    if (!tid) {
      toast.error('Tenant not found');
      return;
    }
    const { application_name, event_name, target_type, selected_task_id, selected_flow_id, execution_mode } = routeForm;
    if (!application_name.trim() || !event_name.trim()) {
      toast.error('Select an application event');
      return;
    }

    let target_reference_id = '';
    let target_name = '';
    let agent_id = '';

    if (target_type === 'TASK') {
      if (!selected_task_id) {
        toast.error('Select an agent task');
        return;
      }
      const picked = taskPickerOptions.find((o) => String(o.id) === String(selected_task_id));
      const task = picked?.task;
      if (!task) {
        toast.error('Selected task not found');
        return;
      }
      target_reference_id = String(task.staticTaskId);
      target_name = staticTaskPickerLabel(task, agentLabelMap);
      agent_id = task.agentName || '';
      if (!agent_id) {
        toast.error('Selected task has no agent');
        return;
      }
    } else if (target_type === 'SYNCFLOW') {
      if (!selected_flow_id) {
        toast.error('Select a Syncflow');
        return;
      }
      const flow = routeDestinations.syncFlows.find((f) => String(f.sfiId) === String(selected_flow_id));
      if (!flow) {
        toast.error('Selected Syncflow not found');
        return;
      }
      target_reference_id = String(flow.sfiId);
      target_name = syncFlowLabel(flow);
    } else if (target_type === 'HUBFLOW') {
      if (!selected_flow_id) {
        toast.error('Select a Hubflow');
        return;
      }
      const flow = routeDestinations.hubFlows.find((f) => String(f.sfiId) === String(selected_flow_id));
      if (!flow) {
        toast.error('Selected Hubflow not found');
        return;
      }
      target_reference_id = String(flow.sfiId);
      target_name = hubFlowLabel(flow);
    }

    setSaving(true);
    try {
      const target_config = target_type === 'TASK' && agent_id ? { agent_id } : {};
      const body = {
        tenant_id: Number(tid),
        application_name: normalizeName(application_name),
        event_name: normalizeName(event_name),
        target_type,
        target_reference_id,
        target_name,
        execution_mode,
        target_config,
        enabled: routeForm.enabled,
      };
      const url = editingRouteId
        ? `${webhookApiBase()}/route-mappings/${encodeURIComponent(editingRouteId)}?tenant_id=${encodeURIComponent(tid)}`
        : `${webhookApiBase()}/route-mappings`;
      const res = await fetch(url, {
        method: editingRouteId ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(editingRouteId ? 'Route mapping updated' : 'Route mapping created');
      setRouteDialogOpen(false);
      setEditingRouteId(null);
      setRouteForm({
        event_key: '',
        application_name: '',
        event_name: '',
        target_type: 'TASK',
        selected_task_id: '',
        selected_flow_id: '',
        execution_mode: 'async',
        enabled: true,
      });
      await loadRoutes();
    } catch (e) {
      toast.error(e.message || 'Failed to save route');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRoute = async (row) => {
    const tid = tenantId();
    if (!tid || !row?.id) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${webhookApiBase()}/route-mappings/${encodeURIComponent(row.id)}?tenant_id=${encodeURIComponent(tid)}`,
        { method: 'DELETE', headers: getAuthHeaders() },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success('Route mapping deleted');
      setDeleteConfirm(null);
      await loadRoutes();
    } catch (e) {
      toast.error(e.message || 'Failed to delete route');
    } finally {
      setSaving(false);
    }
  };

  const copyIngressUrl = async () => {
    const url = publicIngressUrl();
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Ingress URL copied');
    } catch {
      toast.info(url);
    }
  };

  const loadRouteLogs = useCallback(async (routeId) => {
    const tid = tenantId();
    if (!tid || !routeId) return;
    setLoadingRouteLogs(true);
    try {
      const res = await fetch(
        `${webhookApiBase()}/route-mappings/${encodeURIComponent(routeId)}/event-logs?tenant_id=${encodeURIComponent(tid)}&limit=100`,
        { headers: getAuthHeaders() },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setSelectedRouteSummary(data.summary || {
        total_events: 0,
        forwarded_events: 0,
        failed_events: 0,
        received_events: 0,
        last_24h_events: 0,
      });
      setSelectedRouteLogs(Array.isArray(data.logs) ? data.logs : []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load route logs');
      setSelectedRouteLogs([]);
      setSelectedRouteSummary({
        total_events: 0,
        forwarded_events: 0,
        failed_events: 0,
        received_events: 0,
        last_24h_events: 0,
      });
    } finally {
      setLoadingRouteLogs(false);
    }
  }, []);

  const openRouteLogDrawer = async (routeRow) => {
    setSelectedRoute(routeRow);
    setRouteLogDrawerOpen(true);
    await loadRouteLogs(routeRow.id || routeRow.route_mapping_id);
  };

  const openPayloadDialog = (title, payload) => {
    const printable = payload && typeof payload === 'object'
      ? JSON.stringify(payload, null, 2)
      : String(payload || '');
    setPayloadDialogTitle(title);
    setPayloadDialogContent(printable);
    setPayloadDialogOpen(true);
  };

  const subscriptionColumns = useMemo(
    () => [
      { field: 'application_name', headerName: 'Application', width: 130 },
      { field: 'account_identifier', headerName: 'Account Identifier', width: 200 },
      {
        field: 'ctd_id',
        headerName: 'Connector Token',
        width: 120,
        renderCell: (params) => (
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            {params.value ? `${String(params.value).slice(0, 8)}…` : 'manual'}
          </Typography>
        ),
      },
      {
        field: 'enabled',
        headerName: 'Active',
        width: 80,
        renderCell: (params) => (
          <Chip size="small" label={params.value ? 'Yes' : 'No'} color={params.value ? 'success' : 'default'} />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEditSubscriptionDialog(params.row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteConfirm({ type: 'subscription', row: params.row })}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [],
  );

  const routeColumns = useMemo(
    () => [
      { field: 'application_name', headerName: 'Application', width: 150 },
      { field: 'event_name', headerName: 'Event', width: 140 },
      {
        field: 'target_type',
        headerName: 'Destination Type',
        width: 130,
        valueGetter: (_value, row) => {
          const map = { TASK: 'Agent Task', SYNCFLOW: 'Syncflow', HUBFLOW: 'Hubflow' };
          return map[row?.target_type] || row?.target_type;
        },
      },
      { field: 'target_name', headerName: 'Destination', flex: 1, minWidth: 200 },
      { field: 'execution_mode', headerName: 'Mode', width: 80 },
      {
        field: 'enabled',
        headerName: 'Active',
        width: 80,
        renderCell: (params) => (
          <Chip size="small" label={params.value ? 'Yes' : 'No'} color={params.value ? 'success' : 'default'} />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 180,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Button size="small" variant="outlined" onClick={() => openRouteLogDrawer(params.row)}>
              Logs
            </Button>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEditRouteDialog(params.row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteConfirm({ type: 'route', row: params.row })}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [openRouteLogDrawer],
  );

  const eventLogColumns = useMemo(
    () => [
      { field: 'application_name', headerName: 'App', width: 100 },
      { field: 'event_name', headerName: 'Event', width: 120 },
      { field: 'account_identifier', headerName: 'Account', width: 140 },
      {
        field: 'status',
        headerName: 'Status',
        width: 100,
        renderCell: (params) => (
          <Chip size="small" label={params.value} color={statusColor(params.value)} />
        ),
      },
      { field: 'received_at', headerName: 'Received', width: 170 },
      { field: 'target_response_status', headerName: 'Target Status', width: 120 },
      { field: 'failure_stage', headerName: 'Failure Stage', width: 120 },
      { field: 'http_status', headerName: 'HTTP', width: 80 },
      { field: 'error_message', headerName: 'Error', width: 220 },
      { field: 'retry_count', headerName: 'Retries', width: 70 },
      {
        field: 'request_payload',
        headerName: 'Request',
        width: 110,
        sortable: false,
        renderCell: (params) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => openPayloadDialog('Request Payload', params.row.payload)}
          >
            View
          </Button>
        ),
      },
      {
        field: 'response_payload',
        headerName: 'Response',
        width: 110,
        sortable: false,
        renderCell: (params) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => openPayloadDialog('Response Payload', params.row.response_payload)}
          >
            View
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <Paper sx={{ m: 1, p: 2, minHeight: 'calc(100vh - 120px)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Webhook color="primary" />
          <Typography variant="h6">Webhooks</Typography>
          {loading && <CircularProgress size={20} />}
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Tenant webhook routing. Event types are defined globally in{' '}
          <Link component={RouterLink} to="/admin/webhook-event-registry">Webhook Event Registry</Link> (CDX).
        </Alert>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1 }}>
            POST {publicIngressUrl()}
          </Typography>
          <Tooltip title="Copy URL">
            <IconButton size="small" onClick={copyIngressUrl}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Subscriptions" />
          <Tab label="Route Mappings" />
        </Tabs>

        {tab === 0 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" startIcon={<Add />} onClick={openSubscriptionDialog}>
                Add Subscription
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Link this tenant to an application account via connector token.
            </Typography>
            <DynamicDataGrid rows={subscriptions.map((s) => ({ id: s.id, ...s }))} columns={subscriptionColumns} hideFooter />
          </>
        )}

        {tab === 1 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" startIcon={<Add />} onClick={openRouteDialog}>
                Add Route Mapping
              </Button>
            </Box>
            <DynamicDataGrid rows={routes.map((r) => ({ id: r.id, ...r }))} columns={routeColumns} hideFooter />
          </>
        )}

      </Paper>

      <Dialog open={subscriptionDialogOpen} onClose={() => setSubscriptionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSubscriptionId ? 'Edit Subscription' : 'Tenant Subscription'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Application</InputLabel>
            <Select
              label="Application"
              value={subscriptionForm.application_name}
              onChange={(e) => handleSubscriptionApplicationChange(e.target.value)}
            >
              {globalApplications.length === 0 && (
                <MenuItem disabled value="">
                  No global events — add them in CDX → Webhook Event Registry
                </MenuItem>
              )}
              {globalApplications.map((app) => (
                <MenuItem key={app} value={app}>{app}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={!subscriptionForm.application_name || loadingConnectors}>
            <InputLabel>Connector token</InputLabel>
            <Select label="Connector token" value={subscriptionForm.ctd_id} onChange={(e) => handleConnectorSelect(e.target.value)}>
              <MenuItem value=""><em>{loadingConnectors ? 'Loading…' : 'Select connector'}</em></MenuItem>
              {tenantConnectors.map((c) => {
                const id = c.id || c.ctdId;
                return <MenuItem key={id} value={id}>{connectorLabel(c)}</MenuItem>;
              })}
            </Select>
          </FormControl>
          {resolvingAccount && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Reading account from connector token…</Typography>
            </Box>
          )}
          <TextField
            label="Account identifier"
            value={subscriptionForm.account_identifier}
            onChange={(e) => setSubscriptionForm((f) => ({ ...f, account_identifier: e.target.value }))}
            helperText="Auto-filled from connector token. Edit if needed."
            fullWidth
          />
          <FormControlLabel
            control={(
              <Switch
                checked={subscriptionForm.enabled}
                onChange={(e) => setSubscriptionForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
            )}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSubscription} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (editingSubscriptionId ? 'Save' : 'Subscribe')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={routeDialogOpen} onClose={() => setRouteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRouteId ? 'Edit Route Mapping' : 'Route Mapping'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Pick an application event from the global registry and choose where this tenant should forward it.
            Routes are scoped by application + event, so QuickBooks and Gmail can both use names like &quot;bill&quot; safely.
            Multiple routes for the same application event all receive the webhook (fan-out).
            Agent tasks must be Event Based with tools (e.g. gmail_send) assigned in Agent Setup.
          </Typography>
          {loadingRouteDestinations && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading destinations…</Typography>
            </Box>
          )}
          <FormControl fullWidth>
            <InputLabel>Application event</InputLabel>
            <Select
              label="Application event"
              value={routeForm.event_key}
              onChange={(e) => {
                const picked = eventOptions.find((ev) => ev.key === e.target.value);
                setRouteForm((f) => ({
                  ...f,
                  event_key: e.target.value,
                  application_name: picked?.application_name || '',
                  event_name: picked?.event_name || '',
                }));
              }}
            >
              {eventOptions.length === 0 && (
                <MenuItem disabled value="">
                  No events in registry — add them in CDX → Webhook Event Registry
                </MenuItem>
              )}
              {eventOptions.map((ev) => (
                <MenuItem key={ev.key} value={ev.key}>{ev.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Destination type</InputLabel>
            <Select
              label="Destination type"
              value={routeForm.target_type}
              onChange={(e) => setRouteForm((f) => ({
                ...f,
                target_type: e.target.value,
                selected_task_id: '',
                selected_flow_id: '',
              }))}
            >
              <MenuItem value="TASK">Agent Task (event-based)</MenuItem>
              <MenuItem value="SYNCFLOW">Syncflow</MenuItem>
              <MenuItem value="HUBFLOW">Hubflow</MenuItem>
            </Select>
          </FormControl>

          {routeForm.target_type === 'TASK' && (
            <FormControl fullWidth disabled={loadingRouteDestinations}>
              <InputLabel>Agent task</InputLabel>
              <Select
                label="Agent task"
                value={routeForm.selected_task_id}
                onChange={(e) => setRouteForm((f) => ({ ...f, selected_task_id: e.target.value }))}
              >
                {taskPickerOptions.length === 0 && !loadingRouteDestinations && (
                  <MenuItem disabled value="">
                    No event-based tasks — create under AI Agents → Static Tasks (Run type: Event)
                  </MenuItem>
                )}
                {taskPickerOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {routeForm.target_type === 'SYNCFLOW' && (
            <FormControl fullWidth disabled={loadingRouteDestinations}>
              <InputLabel>Syncflow</InputLabel>
              <Select
                label="Syncflow"
                value={routeForm.selected_flow_id}
                onChange={(e) => setRouteForm((f) => ({ ...f, selected_flow_id: e.target.value }))}
              >
                {routeDestinations.syncFlows.length === 0 && (
                  <MenuItem disabled value="">No Syncflow instances for this tenant</MenuItem>
                )}
                {routeDestinations.syncFlows.map((flow) => (
                  <MenuItem key={flow.sfiId} value={flow.sfiId}>
                    {syncFlowLabel(flow)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {routeForm.target_type === 'HUBFLOW' && (
            <FormControl fullWidth disabled={loadingRouteDestinations}>
              <InputLabel>Hubflow</InputLabel>
              <Select
                label="Hubflow"
                value={routeForm.selected_flow_id}
                onChange={(e) => setRouteForm((f) => ({ ...f, selected_flow_id: e.target.value }))}
              >
                {routeDestinations.hubFlows.length === 0 && (
                  <MenuItem disabled value="">No Hubflow instances for this tenant</MenuItem>
                )}
                {routeDestinations.hubFlows.map((flow) => (
                  <MenuItem key={flow.sfiId} value={flow.sfiId}>
                    {hubFlowLabel(flow)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth>
            <InputLabel>Execution mode</InputLabel>
            <Select
              label="Execution mode"
              value={routeForm.execution_mode}
              onChange={(e) => setRouteForm((f) => ({ ...f, execution_mode: e.target.value }))}
            >
              <MenuItem value="async">Async</MenuItem>
              <MenuItem value="sync">Sync</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={(
              <Switch
                checked={routeForm.enabled}
                onChange={(e) => setRouteForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
            )}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRouteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRoute} disabled={saving || loadingRouteDestinations}>
            {saving ? <CircularProgress size={20} /> : (editingRouteId ? 'Save' : 'Save Route')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deleteConfirm?.type === 'subscription'
              ? `Delete subscription for ${deleteConfirm.row?.application_name} / ${deleteConfirm.row?.account_identifier}?`
              : `Delete route ${deleteConfirm?.row?.application_name}/${deleteConfirm?.row?.event_name} → ${deleteConfirm?.row?.target_name || deleteConfirm?.row?.target_type}?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={saving}
            onClick={() => {
              if (deleteConfirm?.type === 'subscription') handleDeleteSubscription(deleteConfirm.row);
              else if (deleteConfirm?.type === 'route') handleDeleteRoute(deleteConfirm.row);
            }}
          >
            {saving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={routeLogDrawerOpen}
        onClose={() => setRouteLogDrawerOpen(false)}
      >
        <Box sx={{ width: 720, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Route Event Logs
          </Typography>
          {selectedRoute && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedRoute.application_name}/{selectedRoute.event_name} → {selectedRoute.target_name || selectedRoute.target_type}
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={`Total: ${selectedRouteSummary.total_events || 0}`} />
            <Chip color="success" label={`Forwarded: ${selectedRouteSummary.forwarded_events || 0}`} />
            <Chip color="error" label={`Failed: ${selectedRouteSummary.failed_events || 0}`} />
            <Chip label={`Received: ${selectedRouteSummary.received_events || 0}`} />
            <Chip label={`Last 24h: ${selectedRouteSummary.last_24h_events || 0}`} />
          </Box>

          <Box sx={{ mb: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={!selectedRoute || loadingRouteLogs}
              onClick={() => loadRouteLogs(selectedRoute.id || selectedRoute.route_mapping_id)}
            >
              {loadingRouteLogs ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Box>

          <DynamicDataGrid
            rows={selectedRouteLogs.map((e) => ({ id: e.id, ...e, error_message: e.error_message || '' }))}
            columns={eventLogColumns}
            hideFooter
          />
        </Box>
      </Drawer>

      <Dialog open={payloadDialogOpen} onClose={() => setPayloadDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{payloadDialogTitle}</DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              maxHeight: 520,
              overflow: 'auto',
              fontSize: 12,
            }}
          >
            {payloadDialogContent || '{}'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayloadDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WebhooksManager;
